/**
 * BoardNoteCard - Note preview card in the notes panel
 */

import { clsx } from 'clsx';
import { StickyNote, Trash2, MoreVertical } from 'lucide-react';
import { formatDistanceToNow } from '@/i18n/dateFns';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { BoardNote } from '@services/api/boardNotes';

interface BoardNoteCardProps {
  note: BoardNote;
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
}

// Get preview text from markdown content
const getContentPreview = (content: string | undefined, maxLength: number = 100): string => {
  if (!content) return 'No content';

  // Strip markdown syntax for preview
  const stripped = content
    .replace(/#{1,6}\s/g, '') // headers
    .replace(/\*\*|__/g, '') // bold
    .replace(/\*|_/g, '') // italic
    .replace(/`{1,3}/g, '') // code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // images
    .replace(/[-*+]\s/g, '') // list items
    .replace(/\n/g, ' ') // newlines
    .trim();

  if (stripped.length <= maxLength) return stripped;
  return stripped.substring(0, maxLength).trim() + '...';
};

export default function BoardNoteCard({
  note,
  isSelected,
  onClick,
  onDelete,
}: BoardNoteCardProps) {
  const preview = getContentPreview(note.content);

  return (
    <div
      onClick={onClick}
      className={clsx(
        'group relative cursor-pointer rounded-lg border p-3',
        'transition-all duration-150',
        isSelected
          ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
          : 'border-neutral-200 bg-white hover:border-neutral-300 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-neutral-600'
      )}
    >
      {/* Header */}
      <div className="mb-1 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <StickyNote
            className={clsx(
              'h-4 w-4 shrink-0',
              isSelected
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-neutral-400 dark:text-neutral-500'
            )}
          />
          <h3
            className={clsx(
              'truncate text-sm font-medium',
              isSelected
                ? 'text-primary-900 dark:text-primary-100'
                : 'text-neutral-900 dark:text-neutral-100'
            )}
            title={note.title}
          >
            {note.title}
          </h3>
        </div>

        {/* Actions dropdown */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className={clsx(
                'rounded p-1',
                'text-neutral-400 hover:text-neutral-600',
                'dark:text-neutral-500 dark:hover:text-neutral-300',
                'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                'opacity-0 group-hover:opacity-100',
                'transition-all'
              )}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={clsx(
                'z-50 min-w-[120px] overflow-hidden rounded-md',
                'bg-white dark:bg-neutral-800',
                'border border-neutral-200 dark:border-neutral-700',
                'p-1 shadow-lg'
              )}
              sideOffset={4}
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu.Item
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className={clsx(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5',
                  'text-sm text-red-600 dark:text-red-400',
                  'hover:bg-red-50 dark:hover:bg-red-900/20',
                  'outline-none'
                )}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      {/* Content preview */}
      <p
        className={clsx(
          'line-clamp-2 text-xs',
          isSelected
            ? 'text-primary-700 dark:text-primary-300'
            : 'text-neutral-500 dark:text-neutral-400'
        )}
      >
        {preview}
      </p>

      {/* Footer */}
      <div className="mt-2 flex items-center justify-between text-xs text-neutral-400 dark:text-neutral-500">
        <span>
          {formatDistanceToNow(new Date(note.updatedAt), { addSuffix: true })}
        </span>
        {note.createdBy && (
          <span className="truncate" title={note.createdBy.displayName}>
            {note.createdBy.displayName}
          </span>
        )}
      </div>
    </div>
  );
}
